import { ArrowLeft, Printer } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateField } from "@/components/DateField";
import { useDiaryMealsRange } from "@/hooks/useDiaryMeals";
import { usePainEntries } from "@/hooks/usePainEntries";
import {
  formatEntryDate,
  formatMeasurementValue,
  localDateOf,
} from "@/lib/body-measurements";
import { addDays } from "@/lib/days";
import { mealTypeLabel } from "@/lib/diary";
import { painLevelAnchor, SYMPTOM_CATALOG } from "@/lib/pain";
import {
  datesBetween,
  formatReportDate,
  isLogged,
  type ReportDay,
  reportDays,
  summarizeReport,
} from "@/lib/report";

// Four weeks: long enough to show a pattern, short enough to print on a few
// pages for a doctor's appointment.
const DEFAULT_DAYS = 28;

/** «1 dag» / «28 dager». */
function dayCount(count: number): string {
  return `${count} ${count === 1 ? "dag" : "dager"}`;
}

export function Report() {
  const navigate = useNavigate();
  const today = localDateOf(new Date());
  const [from, setFrom] = useState<string | undefined>(
    addDays(today, -(DEFAULT_DAYS - 1)),
  );
  const [to, setTo] = useState<string | undefined>(today);

  const valid = from !== undefined && to !== undefined && from <= to;
  const { entries, loading: painLoading } = usePainEntries(
    valid ? from : null,
    valid ? to : null,
  );
  const { days: mealDays, loading: mealsLoading } = useDiaryMealsRange(
    valid ? from : null,
    valid ? to : null,
  );

  const days = valid
    ? reportDays(datesBetween(from, to), entries, mealDays)
    : [];
  const summary = summarizeReport(days);
  const loading = valid && (painLoading || mealsLoading);

  return (
    <div className="report" data-testid="report">
      <div className="report-controls">
        <button
          type="button"
          className="btn ghost icon-btn"
          onClick={() => navigate(-1)}
          data-testid="report-back-button"
        >
          <ArrowLeft size={14} />
          Tilbake
        </button>
        <div className="report-range">
          <label className="setting-label" htmlFor="report-from">
            Fra
          </label>
          <DateField
            id="report-from"
            value={from}
            onChange={setFrom}
            data-testid="report-from-input"
          />
          <label className="setting-label" htmlFor="report-to">
            Til
          </label>
          <DateField
            id="report-to"
            value={to}
            onChange={setTo}
            data-testid="report-to-input"
          />
        </div>
        <button
          type="button"
          className="btn-primary icon-btn"
          disabled={!valid || loading}
          onClick={() => window.print()}
          data-testid="report-print-button"
        >
          <Printer size={14} />
          Skriv ut
        </button>
      </div>

      {!valid ? (
        <p className="field-error" data-testid="report-range-error">
          Velg en fra-dato som ikke er etter til-datoen.
        </p>
      ) : loading ? (
        <p className="hint">Laster…</p>
      ) : (
        <>
          <header className="report-header">
            <h1>Mat og magesmerter</h1>
            <p data-testid="report-period">
              {formatEntryDate(from)} {from.slice(0, 4)} – {formatEntryDate(to)}{" "}
              {to.slice(0, 4)} · {dayCount(summary.dayCount)}
            </p>
          </header>

          <section className="report-summary" data-testid="report-summary">
            <dl>
              <div>
                <dt>Logget</dt>
                <dd data-testid="report-logged-days">
                  {summary.loggedDays} av {dayCount(summary.dayCount)}
                  {summary.dayCount > summary.loggedDays
                    ? ` · ${summary.dayCount - summary.loggedDays} ikke logget`
                    : ""}
                </dd>
              </div>
              <div>
                <dt>Snitt smertenivå</dt>
                <dd data-testid="report-average-level">
                  {summary.averageLevel === null
                    ? "—"
                    : `${formatMeasurementValue(summary.averageLevel)} av 10 (${dayCount(summary.levelDays)} med nivå)`}
                </dd>
              </div>
              <div>
                <dt>Symptomer</dt>
                <dd data-testid="report-symptom-days">
                  {summary.symptomDays
                    .map((s) => `${s.label} ${s.days}`)
                    .join(" · ")}
                  <span className="report-unit"> dager</span>
                </dd>
              </div>
            </dl>
            <PainChart days={days} />
          </section>

          <section className="report-days">
            {days.filter(isLogged).length === 0 ? (
              <p className="hint" data-testid="report-empty">
                Ingenting logget i perioden.
              </p>
            ) : (
              days
                .filter(isLogged)
                .map((day) => <ReportDayView key={day.date} day={day} />)
            )}
          </section>
        </>
      )}
    </div>
  );
}

function ReportDayView({ day }: { day: ReportDay }) {
  const symptoms = SYMPTOM_CATALOG.filter((s) =>
    day.pain?.symptoms.includes(s.key),
  ).map((s) => s.label);
  const level = day.pain?.level;
  return (
    <article className="report-day" data-testid={`report-day-${day.date}`}>
      <h2>{formatReportDate(day.date)}</h2>
      {day.pain ? (
        <div className="report-pain" data-testid={`report-pain-${day.date}`}>
          <p>
            <b>Smertenivå:</b>{" "}
            {typeof level === "number"
              ? `${level} – ${painLevelAnchor(level)}`
              : "ikke satt"}
          </p>
          {symptoms.length > 0 ? (
            <p>
              <b>Symptomer:</b> {symptoms.join(", ")}
            </p>
          ) : null}
          {day.pain.note ? (
            <p className="report-note">
              <b>Notat:</b> {day.pain.note}
            </p>
          ) : null}
        </div>
      ) : null}
      {day.meals.length > 0 ? (
        <ul className="report-meals">
          {day.meals.map((meal) => (
            <li key={meal.id} data-testid={`report-meal-${meal.id}`}>
              <span className="report-meal-when">
                {meal.time} {mealTypeLabel(meal.type)}
              </span>
              <span className="report-meal-what">
                <b>{meal.name ?? meal.description}</b>
                {meal.name && meal.description ? ` (${meal.description})` : ""}
                {meal.ingredients ? (
                  <span className="report-ingredients">
                    {meal.ingredients
                      .map((i) => `${i.name} ${Math.round(i.grams)} g`)
                      .join(", ")}
                  </span>
                ) : (
                  <span className="report-ingredients missing">
                    Ingen ingrediensliste
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="hint">Ingen måltider logget.</p>
      )}
    </article>
  );
}

// Drawn in viewBox units and scaled to the page width, so it prints the
// same at any size.
const CHART = { width: 720, height: 150, left: 28, right: 712, top: 10 };
const CHART_BOTTOM = 120;
const LEVEL_TICKS = [1, 4, 7, 10];

/** One bar per day on the whole 1–10 scale; a day without a level is blank. */
function PainChart({ days }: { days: ReportDay[] }) {
  if (days.length === 0) return null;
  const plotWidth = CHART.right - CHART.left;
  const slot = plotWidth / days.length;
  const barWidth = Math.min(14, slot * 0.7);
  const y = (level: number) =>
    CHART_BOTTOM - (level / 10) * (CHART_BOTTOM - CHART.top);
  // Label Mondays, or every day in a range short enough to fit.
  const labelEvery = days.length <= 14;
  return (
    <svg
      className="report-chart"
      viewBox={`0 0 ${CHART.width} ${CHART.height}`}
      role="img"
      aria-label="Smertenivå per dag, skala 1 til 10"
      data-testid="report-chart"
    >
      {LEVEL_TICKS.map((tick) => (
        <g key={tick}>
          <line
            className="report-chart-grid"
            x1={CHART.left}
            x2={CHART.right}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            className="report-chart-label"
            x={CHART.left - 6}
            y={y(tick) + 4}
            textAnchor="end"
          >
            {tick}
          </text>
        </g>
      ))}
      {days.map((day, i) => {
        const x = CHART.left + slot * i + slot / 2;
        const level = day.pain?.level;
        const monday = new Date(`${day.date}T00:00:00Z`).getUTCDay() === 1;
        return (
          <g key={day.date}>
            {typeof level === "number" ? (
              <rect
                className="report-chart-bar"
                x={x - barWidth / 2}
                y={y(level)}
                width={barWidth}
                height={CHART_BOTTOM - y(level)}
                data-testid={`report-chart-bar-${day.date}`}
              />
            ) : null}
            {labelEvery || monday ? (
              <text
                className="report-chart-label"
                x={x}
                y={CHART_BOTTOM + 16}
                textAnchor="middle"
              >
                {Number(day.date.slice(8, 10))}.{Number(day.date.slice(5, 7))}
              </text>
            ) : null}
          </g>
        );
      })}
      <line
        className="report-chart-frame"
        x1={CHART.left}
        x2={CHART.right}
        y1={CHART_BOTTOM}
        y2={CHART_BOTTOM}
      />
    </svg>
  );
}
